import Image from 'next/image';
import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import clsx from 'clsx';

//types
import type { SubscriptionType } from '@/types/pricingTypes/subscriptionType';
import type { Dispatch, SetStateAction } from 'react';
//utils
import { post } from '@/utils/client';

//apiEndpoints
import { EXTEND_TRIAL } from '@/lib/routes/apiEndpoints';

//hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useUser from '@/utils/hooks/swr/useUser';
import useActiveTreeStructure from '@/utils/hooks/tree-structure/useActiveTreeStructure';

//components
import { RightSection } from '@/components/pages/settings/subscription-invoices/PageContent/Components/RightSection';
import WrapperSettings from '@/components/pages/settings/WrapperSettings';
import SettingsWrapper from '@/components/layouts/LayoutPlatform/components/SettingsWrapper';
import Button from '@/components/base/Button';
import H2 from '@/components/base/Headings/H2';
import { FaqCTA } from '@/components/pages/settings/subscription-invoices/PageContent/Components/FaqCTA';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';

//images
import JellycoGlassesLight from './../../../../../../public/images/pricing/jellyco-glasses-light.webp';
import JellycoGlassesDark from './../../../../../../public/images/pricing/jellyco-glasses-dark.webp';
import JellycoSweatLight from './../../../../../../public/images/pricing/jellyco-sweat-light.webp';
import JellycoSweatDark from './../../../../../../public/images/pricing/jellyco-sweat-dark.webp';

type NoSubscriptionProps = { subscription: SubscriptionType; togglePlanModal: () => void };

type ChoosePlanActionType = {
    onClick: NoSubscriptionProps['togglePlanModal'];
};
function ChoosePlanAction({ onClick }: ChoosePlanActionType) {
    const { t } = useTranslation('settings');

    return (
        <div className="mt-10 flex flex-col items-center space-y-3">
            <Button variant="primary" size="xl" onClick={onClick} data-testid="button-choose-plan">
                {t('subscription_invoices_page.choose_your_plan')}
            </Button>
            <FaqCTA data-testid="choose-plan-faq-link" />
        </div>
    );
}

function ActionsAvailable({
    setExtendTrialError,
    mainButtonAction
}: {
    setExtendTrialError: Dispatch<SetStateAction<boolean>>;
    mainButtonAction: ChoosePlanActionType['onClick'];
}) {
    const [requestLoading, setRequestLoading] = useState(false);
    const { mutateUser } = useUser();
    const { mutateSubscription } = useSubscription();
    const { mutateSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { mutateSpGroups } = useSPGroups({ revalidateIfStale: false });
    const { t } = useTranslation('settings');

    async function handleExtendTrial() {
        setRequestLoading(true);
        try {
            await post(EXTEND_TRIAL);
            await Promise.all([
                mutateSubscription(),
                mutateUser(),
                mutateSocialProfiles(),
                mutateSpGroups()
            ]);
        } catch (e) {
            setExtendTrialError(Boolean(e.message));
        } finally {
            setRequestLoading(false);
        }
    }

    return (
        <div className="mt-10 flex flex-col items-center space-y-3">
            <div className="flex flex-wrap justify-center gap-2">
                <Button
                    data-testid="button-choose-plan"
                    variant="primary"
                    size="xl"
                    onClick={mainButtonAction}
                    disabled={requestLoading}>
                    {t('subscription_invoices_page.choose_your_plan')}
                </Button>
                <Button
                    data-testid="relaunch-trial-button"
                    variant="secondary"
                    size="xl"
                    isLoading={requestLoading}
                    onClick={handleExtendTrial}>
                    {t('subscription_invoices_page.new_pricing.no_subscription.extend_trial')}
                </Button>
            </div>
            <FaqCTA data-testid="choose-plan-faq-link" />
        </div>
    );
}

function NoTrialNeverHadPlan({
    subscription,
    togglePlanModal,
    className
}: NoSubscriptionProps & { className?: string }) {
    const { t } = useTranslation('settings');
    const { isDarkMode } = useDarkMode();
    const [extendTrialError, setExtendTrialError] = useState(false);
    const ImageSrc = isDarkMode() ? JellycoGlassesDark : JellycoGlassesLight;
    const canExtendTrial =
        !subscription.product_key && 'has_extended_trial' in subscription
            ? !subscription.has_extended_trial
            : false;
    return (
        <div
            className={clsx('flex flex-1 flex-col justify-center', className)}
            data-testid="no-subscription-no-plan-history">
            {extendTrialError ? (
                <CompoundBanner type="error" className="mb-8">
                    <CompoundBanner.Content
                        title={t('errors:error_occured')}
                        message={t('errors:error_occured_submit_data')}
                    />
                    <CompoundBanner.Close onClose={() => setExtendTrialError(false)} />
                </CompoundBanner>
            ) : null}
            <div className="flex flex-1 flex-col items-center justify-center">
                <Image src={ImageSrc} width={168} height={175} alt="no active plan illustration" />
                <div className="mt-8 flex flex-col items-center space-y-2.5">
                    <H2>
                        {t('subscription_invoices_page.new_pricing.no_subscription.trial_ended')}
                    </H2>
                    <p className="text-center font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                        <Trans
                            i18nKey="settings:subscription_invoices_page.new_pricing.no_subscription.trial_ended_notice"
                            components={{ br: <br /> }}
                        />
                    </p>
                </div>
                {!canExtendTrial ? (
                    <ChoosePlanAction onClick={togglePlanModal} />
                ) : (
                    <ActionsAvailable
                        setExtendTrialError={setExtendTrialError}
                        mainButtonAction={togglePlanModal}
                    />
                )}
            </div>
        </div>
    );
}

function NoSubscriptionContent({ togglePlanModal }: Pick<NoSubscriptionProps, 'togglePlanModal'>) {
    const { t } = useTranslation('settings');
    const { isDarkMode } = useDarkMode();
    const ImageSrc = isDarkMode() ? JellycoSweatDark : JellycoSweatLight;
    return (
        <div className="flex flex-col items-center">
            <Image src={ImageSrc} width={168} height={175} alt="no active plan illustration" />
            <div className="mt-8 flex flex-col items-center">
                <H2>
                    {t('subscription_invoices_page.new_pricing.no_subscription.previous_plan_end')}
                </H2>
                <p className="text-center font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                    <Trans
                        i18nKey="settings:subscription_invoices_page.new_pricing.no_subscription.previous_plan_end_notice"
                        components={{ br: <br /> }}
                    />
                </p>
            </div>
            <ChoosePlanAction onClick={togglePlanModal} />
        </div>
    );
}

export function NoSubscription({ subscription, togglePlanModal }: NoSubscriptionProps) {
    const { isTreeStructureActivated } = useActiveTreeStructure();

    if (subscription?.no_trial_never_had_plan) {
        if (isTreeStructureActivated) {
            return (
                <SettingsWrapper.MainContent>
                    <NoTrialNeverHadPlan
                        subscription={subscription}
                        togglePlanModal={togglePlanModal}
                    />
                </SettingsWrapper.MainContent>
            );
        }
        return (
            <NoTrialNeverHadPlan
                subscription={subscription}
                togglePlanModal={togglePlanModal}
                className="p-8 xl:px-20 xl:py-10"
            />
        );
    }

    if (isTreeStructureActivated) {
        return (
            <>
                <SettingsWrapper.MainContent>
                    <NoSubscriptionContent togglePlanModal={togglePlanModal} />
                </SettingsWrapper.MainContent>
                <RightSection togglePlanModal={togglePlanModal} />
            </>
        );
    }

    return (
        <>
            <WrapperSettings.WrapperSettingsFirstColumn
                className="flex flex-1 items-center justify-center p-8 xl:px-20 xl:py-10"
                data-testid="no-subscription-plan-history">
                <NoSubscriptionContent togglePlanModal={togglePlanModal} />
            </WrapperSettings.WrapperSettingsFirstColumn>
            <RightSection togglePlanModal={togglePlanModal} />
        </>
    );
}
